/**
 * PlatformProvider
 * 
 * React контекст для работы с разными платформами (Telegram/VK/Web)
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { detectPlatform, Platform } from './detect';
import { vkPlatform } from './vk';
import { telegramPlatform } from './telegram';
import { webPlatform } from './web';

// ============================================================================
// TYPES
// ============================================================================

interface PlatformContextValue {
  /** Текущая платформа */
  platform: Platform;
  
  /** Приложение готово к работе */
  isReady: boolean;
  
  /** ID пользователя для API (tg_123, vk_456, br_xxx) */
  visibleId: string | null;
  
  /** Числовой ID (для Telegram/VK) */
  numericId: number | null;
  
  /** Имя пользователя */
  displayName: string;
  
  /** URL аватара */
  avatar: string | null;
  
  /** Методы */
  haptic: (type?: 'impact' | 'notification' | 'selection') => void;
  share: (text: string, url?: string) => void;
  showAlert: (message: string) => void;
  close: () => void;
  
  /** Открыть в другой платформе */
  openTelegram: (path?: string) => void;
  openVK: (path?: string) => void;
}

// ============================================================================
// CONTEXT
// ============================================================================

const PlatformContext = createContext<PlatformContextValue | null>(null);

// ============================================================================
// VK APP ID
// ============================================================================

const VK_APP_ID = '54439669';

// ============================================================================
// PROVIDER
// ============================================================================

interface PlatformProviderProps {
  children: ReactNode;
}

export function PlatformProvider({ children }: PlatformProviderProps) {
  const [platform] = useState<Platform>(() => detectPlatform());
  const [isReady, setIsReady] = useState(false);
  const [visibleId, setVisibleId] = useState<string | null>(null);
  const [numericId, setNumericId] = useState<number | null>(null);
  const [displayName, setDisplayName] = useState('Гость');
  const [avatar, setAvatar] = useState<string | null>(null);
  
  // Инициализация платформы
  useEffect(() => {
    async function init() {
      console.log('[Platform] Detected:', platform);
      
      try {
        switch (platform) {
          case 'vk': {
            await vkPlatform.init();
            const user = await vkPlatform.getUser();
            setNumericId(user.id);
            setVisibleId(`vk_${user.id}`);
            setDisplayName(vkPlatform.getDisplayName());
            setAvatar(vkPlatform.getAvatar());
            break;
          }
          
          case 'telegram': {
            await telegramPlatform.init();
            const tgId = telegramPlatform.getUserId();
            if (tgId) {
              setNumericId(tgId);
              setVisibleId(`tg_${tgId}`);
              setDisplayName(telegramPlatform.getDisplayName());
            } else {
              // Telegram но без данных пользователя
              const browserId = webPlatform.getBrowserId();
              setVisibleId(browserId);
            }
            break;
          }
          
          case 'web':
          case 'embed': {
            await webPlatform.init();
            const browserId = webPlatform.getBrowserId();
            setVisibleId(browserId);
            setDisplayName('Гость');
            break;
          }
        }
      } catch (error) {
        console.error('[Platform] Init error:', error);
        // Fallback to web
        const browserId = webPlatform.getBrowserId();
        setVisibleId(browserId);
      }
      
      setIsReady(true);
      console.log('[Platform] Ready');
    }
    
    init();
  }, [platform]);
  
  // Методы
  const haptic = (type: 'impact' | 'notification' | 'selection' = 'selection') => {
    switch (platform) {
      case 'vk':
        vkPlatform.haptic(type);
        break;
      case 'telegram':
        telegramPlatform.haptic(type);
        break;
    }
  };
  
  const share = (text: string, url?: string) => {
    switch (platform) {
      case 'vk':
        vkPlatform.share(url || window.location.href);
        break;
      case 'telegram':
        telegramPlatform.share(text, url);
        break;
      default:
        webPlatform.share(text, url);
    }
  };
  
  const showAlert = (message: string) => {
    switch (platform) {
      case 'vk':
        vkPlatform.showAlert(message);
        break;
      case 'telegram':
        telegramPlatform.showAlert(message);
        break;
      default:
        webPlatform.showAlert(message);
    }
  };
  
  const close = () => {
    switch (platform) {
      case 'vk':
        vkPlatform.close();
        break;
      case 'telegram':
        telegramPlatform.close();
        break;
    }
  };
  
  const openTelegram = (path: string = '') => {
    window.open(`https://t.me/qwizmug_bot${path}`, '_blank');
  };
  
  const openVK = (path: string = '') => {
    window.open(`https://vk.com/app${VK_APP_ID}${path}`, '_blank');
  };
  
  const value: PlatformContextValue = {
    platform,
    isReady,
    visibleId,
    numericId,
    displayName,
    avatar,
    haptic,
    share,
    showAlert,
    close,
    openTelegram,
    openVK,
  };
  
  // Показываем loading пока инициализируется
  if (!isReady) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-xl">Загрузка...</div>
      </div>
    );
  }
  
  return (
    <PlatformContext.Provider value={value}>
      {children}
    </PlatformContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function usePlatform(): PlatformContextValue {
  const context = useContext(PlatformContext);
  if (!context) {
    throw new Error('usePlatform must be used within PlatformProvider');
  }
  return context;
}

// ============================================================================
// HELPER HOOKS
// ============================================================================

/** Получить заголовки для API запросов */
export function useApiHeaders(): Record<string, string> {
  const { platform, numericId, visibleId, displayName } = usePlatform();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  if (platform === 'telegram' && numericId) {
    headers['X-Telegram-User-Id'] = String(numericId);
    headers['X-User-Name'] = displayName;
  } else if (platform === 'vk' && numericId) {
    headers['X-VK-User-Id'] = String(numericId);
    headers['X-User-Name'] = displayName;
  } else if (visibleId) {
    headers['X-Browser-Id'] = visibleId;
  }
  
  return headers;
}
